import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { AlertTriangle, Search, MapPin, Loader2, Check } from "lucide-react";
import { getProduttori, createGiroOverride } from "../../lib/api";

/**
 * Blocco URGENZA/EXTRA — Modale per aggiungere un produttore come EXTRA/URGENZA
 * a un giro operativo (source="zone").
 *
 * Logica:
 *  - L'ufficio cerca un produttore (search lato server)
 *  - Mostra zona originale del produttore
 *  - Evidenzia se zona ≠ zona del giro (badge "ALTRA ZONA")
 *  - Se il giro è IN_CORSO chiede conferma extra
 *  - Crea override AGGIUNGI_EXTRA (data_giro, produttore_id, mezzo_destinazione_id, motivo)
 *  - NESSUNA modifica al produttore (zona / frequenza / W invariati)
 */
export function AggiungiExtraDialog({
  open,
  onClose,
  giro, // {giroId, mezzo: {id, nome}, zona, stato, fermate: [{id, ragioneSociale,...}]}
  dataGiro,
  onSuccess,
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);
  const [risultati, setRisultati] = useState([]);
  const [selected, setSelected] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmInCorso, setConfirmInCorso] = useState(false);

  const giroZona = giro?.zona || "";
  const giroStato = giro?.stato || "PIANIFICATO";
  const mezzoTarget = giro?.mezzo;
  const stopIdsInGiro = useMemo(
    () => new Set((giro?.fermate || []).map((s) => s.id)),
    [giro]
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebounced("");
      setRisultati([]);
      setSelected(null);
      setMotivo("");
      setSubmitting(false);
      setConfirmInCorso(false);
    }
  }, [open]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    if (debounced.length < 2) {
      setRisultati([]);
      return;
    }
    let cancel = false;
    setLoading(true);
    getProduttori({ q: debounced, attivo: true, limit: 20 })
      .then((d) => {
        if (cancel) return;
        // l'endpoint può rispondere {items, total} oppure direttamente lista
        const items = Array.isArray(d) ? d : d.items || [];
        setRisultati(items);
      })
      .catch(() => {
        if (!cancel) setRisultati([]);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [debounced, open]);

  const doSubmit = async () => {
    if (!selected || !giro?.giroId) return;
    setSubmitting(true);
    try {
      const res = await createGiroOverride({
        data_giro: dataGiro,
        produttore_id: selected.id,
        tipo: "AGGIUNGI_EXTRA",
        giro_destinazione_id: giro.giroId,
        ...(mezzoTarget?.id ? { mezzo_destinazione_id: mezzoTarget.id } : {}),
        motivo: motivo.trim() || "",
      });
      toast.success(`${selected.ragioneSociale} aggiunto come urgenza al giro`);
      onSuccess?.(res);
      onClose();
    } catch (ex) {
      const msg = ex?.response?.data?.detail || "Errore aggiunta urgenza";
      // 409 = duplicato, 400 = stato giro, 404 = mezzo
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConferma = () => {
    if (giroStato === "IN_CORSO" && !confirmInCorso) {
      setConfirmInCorso(true);
      return;
    }
    doSubmit();
  };

  const isAlreadyInGiro = selected && stopIdsInGiro.has(selected.id);
  const isZonaDiversa = selected && (selected.zona || "") !== giroZona;
  const canSubmit =
    !!selected &&
    !!giro?.giroId &&
    !isAlreadyInGiro &&
    !submitting;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="dialog-aggiungi-extra"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-[#F57C00]" />
            Aggiungi urgenza / extra
          </DialogTitle>
          <DialogDescription>
            Aggiungi un produttore come fermata extra a questo giro operativo.
            Non verranno modificate zona, frequenza o pianificazione del produttore.
          </DialogDescription>
        </DialogHeader>

        {!giro ? null : (
          <div className="space-y-4">
            {/* Contesto giro */}
            <Card className="p-3 bg-[#7B1FA2]/5 border border-[#7B1FA2]/20">
              <div className="text-xs text-slate-700 space-y-0.5" data-testid="extra-context">
                <div>
                  <span className="font-semibold">Giro:</span>{" "}
                  <span className="font-mono">{giro.zona}</span>
                  {" · "}
                  <span className="font-mono">{dataGiro}</span>
                </div>
                <div>
                  <span className="font-semibold">Mezzo:</span>{" "}
                  {mezzoTarget?.id ? (
                    <span>{mezzoTarget.nome}</span>
                  ) : (
                    <span className="text-[#C62828]">— Nessun mezzo assegnato —</span>
                  )}
                </div>
                <div>
                  <span className="font-semibold">Stato:</span>{" "}
                  <Badge
                    variant="outline"
                    className="text-[10px]"
                    data-testid="extra-stato-giro"
                  >
                    {giroStato}
                  </Badge>
                </div>
              </div>
            </Card>

            {!mezzoTarget?.id && (
              <Card className="p-3 bg-[#F57C00]/10 border border-[#F57C00]/30">
                <div className="text-xs text-[#E65100] flex items-center gap-2">
                  <AlertTriangle size={14} />
                  Nessun mezzo assegnato: l&apos;urgenza verrà collegata al giro e
                  resterà valida anche dopo l&apos;assegnazione del mezzo.
                </div>
              </Card>
            )}

            {/* Search produttore */}
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Cerca produttore
              </Label>
              <div className="relative mt-1">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <Input
                  autoFocus
                  className="pl-8"
                  placeholder="Digita ragione sociale (min 2 caratteri)"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(null);
                  }}
                  data-testid="extra-search-input"
                />
              </div>
              {debounced.length >= 2 && (
                <div
                  className="mt-2 border border-slate-200 rounded-md max-h-56 overflow-y-auto bg-white"
                  data-testid="extra-results-list"
                >
                  {loading ? (
                    <div className="text-xs text-slate-500 p-3 flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin" />
                      Caricamento…
                    </div>
                  ) : risultati.length === 0 ? (
                    <div
                      className="text-xs text-slate-500 p-3 italic"
                      data-testid="extra-results-empty"
                    >
                      Nessun produttore corrisponde a &quot;{debounced}&quot;.
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {risultati.map((p) => {
                        const alreadyIn = stopIdsInGiro.has(p.id);
                        const isSel = selected?.id === p.id;
                        return (
                          <li key={p.id}>
                            <button
                              type="button"
                              disabled={alreadyIn}
                              onClick={() => setSelected(p)}
                              className={`w-full text-left p-2 hover:bg-slate-50 flex items-start gap-2 ${
                                isSel ? "bg-[#7B1FA2]/10" : ""
                              } ${alreadyIn ? "opacity-50 cursor-not-allowed" : ""}`}
                              data-testid={`extra-result-${p.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-900 truncate">
                                  {p.ragioneSociale}
                                </div>
                                <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                                  <MapPin size={10} />
                                  {p.indirizzo}
                                  {p.citta ? `, ${p.citta}` : ""}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  Zona:{" "}
                                  <span className="font-semibold">
                                    {p.zona || "—"}
                                  </span>
                                </div>
                              </div>
                              {alreadyIn && (
                                <Badge
                                  className="bg-slate-400 text-white text-[9px] shrink-0"
                                  data-testid={`extra-already-in-${p.id}`}
                                >
                                  GIÀ NEL GIRO
                                </Badge>
                              )}
                              {isSel && (
                                <Check size={14} className="text-[#7B1FA2] shrink-0" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Selected info */}
            {selected && (
              <Card
                className="p-3 bg-[#F57C00]/5 border border-[#F57C00]/30"
                data-testid="extra-selected-card"
              >
                <div className="text-xs space-y-1">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {selected.ragioneSociale}
                    {isAlreadyInGiro && (
                      <Badge className="bg-[#C62828] text-white text-[10px]">
                        GIÀ NEL GIRO
                      </Badge>
                    )}
                    {!isAlreadyInGiro && isZonaDiversa && (
                      <Badge
                        className="bg-[#1565C0] text-white text-[10px]"
                        data-testid="extra-zona-diversa-badge"
                      >
                        ALTRA ZONA
                      </Badge>
                    )}
                  </div>
                  <div className="text-slate-600">
                    Zona originale:{" "}
                    <span className="font-mono">{selected.zona || "—"}</span>
                    {isZonaDiversa && (
                      <span className="text-slate-500">
                        {" "}
                        (giro: <span className="font-mono">{giroZona}</span>)
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Motivo */}
            <div>
              <Label className="text-xs uppercase tracking-wider">
                Motivo / note (opzionale)
              </Label>
              <Input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="es. cliente ha chiamato, urgenza, ritiro non programmato…"
                maxLength={200}
                data-testid="extra-motivo-input"
              />
            </div>

            {/* In-corso conferma */}
            {confirmInCorso && (
              <Card
                className="p-3 bg-[#F57C00]/10 border border-[#F57C00]/30"
                data-testid="extra-conferma-incorso"
              >
                <div className="text-xs text-[#E65100] flex items-start gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    Il giro è già in corso. Confermi di voler aggiungere questa
                    fermata urgente? Il trasportatore la vedrà al prossimo refresh.
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="extra-cancel-btn"
          >
            Annulla
          </Button>
          <Button
            onClick={handleConferma}
            disabled={!canSubmit}
            className="bg-[#F57C00] hover:bg-[#E65100] text-white"
            data-testid="extra-conferma-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-1 animate-spin" />
                Aggiunta in corso…
              </>
            ) : confirmInCorso ? (
              "Sì, aggiungi comunque"
            ) : (
              "Aggiungi urgenza"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
